/**
 =========================================================
 * Material Dashboard 2 React - v2.1.0
 =========================================================
 
 * Product Page: https://www.creative-tim.com/product/material-dashboard-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)
 
 Coded by www.creative-tim.com
 
 =========================================================
 
 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Select from "react-select";
import UserManagementApi from "../../../../../Classes/API/Administration/UserManagementApi";

import { useContext } from "react";
import { UserContext } from "App";

import { SelectedUserContext } from "../../index";
import "./user-setting.scss";
import UserTypes1 from "../../../../../Classes/Models/Requests/Users/UserTypes";
import translations from "layouts/Appointments/Feedback/translations";
import { LanguageContext } from "App";
import { jwtDecode } from "jwt-decode";
import { getAuth, signOut } from "firebase/auth";

function PlatformSettings({
  SoftDeleteUser,
  ToggleUserActivation,
  EditUser,
  AssignTypeToUser,
  ToggleSuperAdmin,
}) {
  let userTypes = new UserTypes1();
  const User = useContext(SelectedUserContext);
  const CurrentUser = useContext(UserContext);
  const { translations, switchLanguage } = useContext(LanguageContext);

  // First define all constants and options
  const UnAssignedUserType = {
    value: userTypes.UnAssigned,
    label: translations.userManagementInfoUserSettingSetUserTypeNotassigned,
  };

  const UserTypeOptions = [
    { value: userTypes.Admin, label: "Admin" },
    { value: userTypes.SubAdmin, label: "Sub Admin" },
    { value: userTypes.AppointmentEmployee, label: "Appointment Employee" },
    { value: userTypes.AccountingEmployee, label: "Accounting Employee" },
    { value: userTypes.Patient, label: "Patient" },
    { value: userTypes.Therapist, label: "Therapist" },
    { value: "FloorManager", label: "Floor Manager" },
  ];

  const NoNationality = {
    value: "",
    label: translations.userManagementInfoUserSettingSetUserTypeNotassigned,
  };

  const NationalityGroupOptions = [
    { value: "Egyptian", label: "Egyptian" },
    { value: "Arab", label: "Arab" },
    { value: "Other", label: "Other" },
  ];

  // Then define helper functions that use these constants
  const GetDefaultNationGroupOption = () => {
    if (User.nationalityGroup) {
      if (User.nationalityGroup == "Egyptian") {
        return NationalityGroupOptions[0];
      }
      if (User.nationalityGroup == "Arab") {
        return NationalityGroupOptions[1];
      }
      if (User.nationalityGroup == "Other") {
        return NationalityGroupOptions[2];
      }
    }
    return NoNationality;
  };

  const GetDefaultUserTypeOptions = () => {
    if (User.userType == userTypes.UnAssigned) {
      return UnAssignedUserType;
    }
    return { value: User.userType, label: User.userType };
  };

  // Then declare state that uses these functions
  const [selectedNationalityOption, setSelectedNationalityOption] = useState(
    GetDefaultNationGroupOption()
  );
  const [selectedUserTypeOption, setSelectedUserTypeOption] = useState(GetDefaultUserTypeOptions());
  const [isSuperAdminUser, setIsSuperAdminUser] = useState(false);
  const [decodedTenant, setDecodedTenant] = useState("");

  const EditNationalityGroup = () => {
    let editedUser = User;
    editedUser.nationalityGroup = selectedNationalityOption.value;
    EditUser(editedUser);
  };

  const EditUserType = () => {
    let editedUser = User;
    editedUser.userType = selectedUserTypeOption.value;
    AssignTypeToUser(editedUser);
  };

  useEffect(() => {
    const checkSuperAdminStatus = async () => {
      try {
        if (User?.firebaseId) {
          const isSuperAdmin = await UserManagementApi.IsSuperAdmin(User.firebaseId);
          setIsSuperAdminUser(isSuperAdmin);
        }
      } catch (error) {
        console.error("Error checking Super Admin status:", error);
        // Optionally show error to user
        Swal.fire({
          title: "Error",
          text: error.message,
          icon: "error",
        });
      }
    };

    checkSuperAdminStatus();
  }, [User?.firebaseId]);

  const auth = getAuth();
  const [user, loading, error] = useAuthState(auth);
  useEffect(() => {
    if (!CurrentUser?.id) return;

    try {
      const token = auth.currentUser?.accessToken;
      if (token) {
        const decoded = jwtDecode(token);
        setDecodedTenant(decoded?.tenant);
      }
    } catch (error) {
      console.error("Error decoding token:", error);
    }
  }, [CurrentUser?.id, auth.currentUser]);
  const handleToggleSuperAdmin = async () => {
    try {
      const newStatus = await ToggleSuperAdmin(User.id);
      setIsSuperAdminUser(newStatus);
    } catch (error) {
      console.error("Error toggling Super Admin:", error);
    }
  };

  return (
    <Card sx={{ boxShadow: "none" }}>
      <MDBox p={2}>
        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
          {translations.userManagementInfoUserSettingUserSettings}
        </MDTypography>
      </MDBox>
      <MDBox pt={1} pb={2} px={2} lineHeight={1.25}>
        <MDTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase">
          {translations.userManagementInfoUserSettingaccount}
        </MDTypography>
        <MDBox display="flex" alignItems="center" mb={0.5} ml={-1.5}>
          <MDBox mt={0.5}>
            <Switch checked={User.isActivated} onChange={() => ToggleUserActivation(User.id)} />
          </MDBox>
          <MDBox width="80%" ml={0.5}>
            <MDTypography variant="button" fontWeight="regular" color="text">
              {translations.userManagementInfoUserSettingActivated}
            </MDTypography>
          </MDBox>
        </MDBox>
        {decodedTenant === "superadmin" && CurrentUser?.id !== User?.id && (
          <MDBox display="flex" alignItems="center" mb={0.5} ml={-1.5}>
            <MDBox mt={0.5}>
              <Switch checked={isSuperAdminUser} onChange={handleToggleSuperAdmin} />
            </MDBox>
            <MDBox width="80%" ml={0.5}>
              <MDTypography variant="button" fontWeight="regular" color="text">
                Super Admin
              </MDTypography>
            </MDBox>
          </MDBox>
        )}
        <MDBox display="flex" alignItems="center" mb={0.5} ml={-1.5}>
          <MDBox mt={0.5}>
            <Switch checked={User.isSoftDeleted} onChange={() => SoftDeleteUser(User.id)} />
          </MDBox>
          <MDBox width="80%" ml={0.5}>
            <MDTypography variant="button" fontWeight="regular" color="text">
              {translations.userManagementInfoUserSettingSoftDeleted}
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox display="flex" alignItems="center" mb={0.5} ml={-1.5}>
          <MDBox width="80%" ml={0.5}>
            <MDTypography variant="button" fontWeight="regular" color="text">
              {translations.userManagementInfoUserSettingSetUserNationalityGroup}
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox className="nationality-input" mb={0.5} ml={-1.5}>
          <MDBox mt={0.5}>
            <Select
              isClearable={true}
              value={selectedNationalityOption}
              onChange={(selectedOption) => setSelectedNationalityOption(selectedOption)}
              options={NationalityGroupOptions}
              styles={{
                container: (provided) => ({
                  ...provided,
                  width: "200px",
                }),
                menu: (provided) => ({
                  ...provided,
                  width: "200px",
                  zIndex: 9999,
                }),
              }}
              menuPlacement="auto"
              isSearchable={false}
            />
          </MDBox>
          <div>
            <button onClick={() => EditNationalityGroup()} className="btn btn-outline-dark ml-3">
              Save
            </button>
          </div>
        </MDBox>

        <MDBox className="nationality-input" mb={0.5} ml={-1.5}>
          <MDBox width="80%" ml={0.5}>
            <MDTypography variant="button" fontWeight="regular" color="text">
              {translations.userManagementInfoUserSettingSetUserType}
            </MDTypography>
          </MDBox>
        </MDBox>
        {User.userType == userTypes.UnAssigned ? (
          <MDBox display="flex" alignItems="center" mb={0.5} ml={-1.5}>
            <MDBox className="nationality-input" mb={0.5} ml={-1.5}>
              <MDBox mt={0.5}>
                <Select
                  isClearable={true}
                  value={selectedUserTypeOption}
                  onChange={(selectedOption) => setSelectedUserTypeOption(selectedOption)}
                  options={UserTypeOptions}
                  styles={{
                    container: (provided) => ({
                      ...provided,
                      width: "200px",
                      marginLeft: "10px",
                    }),
                    menu: (provided) => ({
                      ...provided,
                      width: "200px",
                      zIndex: 9999,
                    }),
                  }}
                  menuPlacement="auto"
                  isSearchable={false}
                />
              </MDBox>
              <div>
                <button onClick={() => EditUserType()} className="btn btn-outline-dark ml-3">
                  Save
                </button>
              </div>
            </MDBox>
          </MDBox>
        ) : (
          User.userType
        )}
      </MDBox>
    </Card>
  );
}

export default PlatformSettings;
